import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { ChatCircleDotsIcon } from '@phosphor-icons/react';
import { Popover, PopoverContent, PopoverTrigger } from '~/components/ui/popover';
import { Tip } from '~/components/ui/tip';
import { useCodingEnabled } from '~/hooks/queries/use-tenant';
import { useProBuilderEnabled } from '~/hooks/queries/use-feature-flags';
import { useChatActivity } from '~/features/chat/top-bar/use-chat-activity';
import { useOpenBuild } from '~/features/chat/top-bar/use-open-build';
import { statusLineFor, type BarChat } from '~/features/chat/top-bar/state-mapping';
import { originToReturnTo } from '~/lib/chat-origin';
import { useAttentionStore } from '~/lib/stores/attention-store';
import { useChatOpenStore } from '~/lib/stores/chat-open-store';
import { useProBuilderOpenStore } from '~/lib/stores/pro-builder-open-store';

/**
 * FRONT-3143: the chats, builds and Pro Builder session waiting on the person's
 * answer, for the sidebar. Same data as the activity board's "Asking you" group
 * (`useChatActivity`, with builds and Pro Builder folded in exactly as the top
 * bar did), so the number can never disagree with what the board used to say.
 *
 * It also publishes the count to the attention store, which the app shell reads
 * for the browser tab's "(N)" prefix and favicon. The top bar used to be the
 * publisher; the sidebar is mounted on every page, so it is the publisher now.
 */
export function useSidebarAsking(): BarChat[] {
  const codingEnabled = useCodingEnabled();
  const proBuilderEnabled = useProBuilderEnabled();
  const { barChats } = useChatActivity({ includeBuilds: codingEnabled, includeProBuilder: proBuilderEnabled });
  const asking = useMemo(() => barChats.filter((c) => c.state === 'asking'), [barChats]);
  const setAsking = useAttentionStore((s) => s.setAsking);
  useEffect(() => {
    setAsking(asking.length);
  }, [asking.length, setAsking]);
  useEffect(() => () => setAsking(0), [setAsking]);
  return asking;
}

/** Opens one waiting item: a chat, a Building Session or the Pro Builder window. */
function useOpenAsking(): (chat: BarChat) => void {
  const location = useLocation();
  const navigate = useNavigate();
  const requestOpenChat = useChatOpenStore((s) => s.requestOpenChat);
  const openBuild = useOpenBuild();
  return (chat) => {
    if (chat.kind === 'pro-builder') {
      useProBuilderOpenStore.getState().openProBuilder();
    } else if (chat.kind === 'build') {
      openBuild(chat.sessionId);
    } else {
      // Same one-modal-owner route the header's chat opens used: navigate Home
      // if elsewhere, then let Home's single launcher open the thread.
      const returnTo = originToReturnTo(location);
      if (returnTo !== null) navigate('/');
      requestOpenChat(chat.sessionId, chat.title, returnTo);
    }
  };
}

/**
 * "Asking you" — rendered only while something is waiting. A badge in the
 * rail means WAITING ON YOU (CONTEXT.md, attention badge), so at zero this
 * renders nothing rather than a quiet "0".
 *
 * Collapsed rail only: one badge; one item opens straight away, several open
 * a short list to pick from. The expanded sidebar shows no rows at all — just
 * the amber count on the Inbox row (eddy, 2026-10-26); the items themselves
 * are in the top bar's question chips and Today's "Waiting on you" group.
 */
export function SidebarAskingRow({ asking }: { asking: BarChat[] }) {
  const [open, setOpen] = useState(false);
  const openAsking = useOpenAsking();
  if (asking.length === 0) return null;
  const label = `Asking you: ${asking.length}`;

  function pick(chat: BarChat) {
    setOpen(false);
    openAsking(chat);
  }

  const trigger = (
    <button
      type="button"
      aria-label={label}
      data-testid="sidebar-asking"
      className="relative flex w-full items-center justify-center rounded-lg px-1.5 py-2 text-amber-600 transition-colors hover:bg-[var(--bg-3)] dark:text-amber-400"
    >
      <ChatCircleDotsIcon size={16} weight="regular" />
      <span className="absolute right-0.5 top-0.5 min-w-[14px] rounded-full bg-amber-500 px-1 text-center font-mono text-[9px] font-semibold leading-[14px] text-white">
        {asking.length}
      </span>
    </button>
  );

  // One item: no list to pick from. The popover trigger is the only asChild
  // here, so no Tip wraps it (a Tip around a PopoverTrigger drops its props).
  if (asking.length === 1) {
    const only = asking[0]!;
    const body = (
      <div className="pb-0.5" onClick={() => openAsking(only)}>
        {trigger}
      </div>
    );
    return <Tip label={`Asking you: ${only.title}`} side="right">{body}</Tip>;
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent side="right" align="start" className="w-80 p-1">
        <p className="px-2 py-1.5 text-[length:var(--text-2xs)] font-medium uppercase tracking-wide text-muted-foreground">
          Asking you
        </p>
        <ul className="max-h-80 overflow-y-auto">
          {asking.map((chat) => (
            <li key={`${chat.kind}:${chat.sessionId}`}>
              <button
                type="button"
                onClick={() => pick(chat)}
                className="flex w-full flex-col gap-0.5 rounded-md px-2 py-1.5 text-left hover:bg-[var(--bg-3)]"
              >
                <span className="truncate text-[13px] text-[var(--ink-0)]">{chat.title}</span>
                <span className="line-clamp-2 text-[length:var(--text-2xs)] text-muted-foreground">
                  {statusLineFor(chat)}
                </span>
              </button>
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
